import { useContext, useEffect, useState } from 'react'
import DropDown from '../../atoms/DropDown/dropdown'
import { ApiContext } from '../../../contexts/api-context'
import { ReportingContext } from '../../../contexts/reporting-context'
import { ListOption, ReportDateRange, Team } from '../../../types/andTypes'
import { TeamExtensions } from '../../../constants'
import logAnalyticsEvent from '../../../utilities/firebase-analytics'
import { DropDownFilter, FilterContainer } from './reporting-filter-styles'
import ComboBox from '../../../components/atoms/ComboBox/combobox'
import dayjs, { Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import quarterOfYear from 'dayjs/plugin/quarterOfYear'

dayjs.extend(utc)
dayjs.extend(quarterOfYear)

const ReportingFilter = () => {
  const { businessUnits, userProfile } = useContext(ApiContext)
  const [selectedBU, setSelectedBU] = useState<string>()
  const {
    reportBusinessUnit,
    reportDateRange,
    reportTeam,
    setReportBusinessUnit,
    setReportDateRange,
    setReportTeam
  } = useContext(ReportingContext)
  const [selectedTeam, setSelectedTeam] = useState<string>()
  const [teamOptions, setTeamOptions] = useState<Team[]>([])
  const [dateOptions, setDateOptions] = useState<ReportDateRange[]>([])
  const [selectedDate, setSelectedDate] = useState<string>()

  useEffect(() => {
    setSelectedBU(userProfile?.businessUnit)
    setSelectedTeam(TeamExtensions.ALL_TEAMS.id)
    setSelectedDate('last-30-days')
  }, [userProfile])

  useEffect(() => {
    updateTeamOptions()
    setSelectedTeam(TeamExtensions.ALL_TEAMS.id)
    if (selectedBU !== reportBusinessUnit?.id) {
      setReportBusinessUnit(businessUnits.find(bu => bu.id === selectedBU))
    }
    logAnalyticsEvent('business_unit_filter')
  }, [selectedBU])

  useEffect(() => {
    if (selectedTeam !== reportTeam?.id) {
      setReportTeam(teamOptions.find(team => team.id === selectedTeam))
    }
    logAnalyticsEvent('team_filter')
  }, [selectedTeam])

  useEffect(() => {
    updateDateOption()
    if (selectedDate !== reportDateRange?.id) {
      setReportDateRange(dateOptions.find(range => range.id === selectedDate))
    }
    logAnalyticsEvent('date_filter')
  }, [selectedDate])

  const updateTeamOptions = () => {
    const selectedBusinessUnit = businessUnits.find(bU => bU.id === selectedBU)
    const teams = selectedBusinessUnit?.teams || []
    const teamsArray = [
      TeamExtensions.ALL_TEAMS,
      TeamExtensions.OTHER_BUSINESS_UNIT,
      TeamExtensions.UNDEFINED_TEAM
    ]
    teams.forEach(team => {
      teamsArray.push(team)
    })
    setTeamOptions(teamsArray)
  }

  const updateDateOption = () => {
    const dateList = [
      { id: 'last-week', name: 'Last Week (Mon-Fri)' },
      { id: 'last-30-days', name: 'Last 30 days' },
      { id: 'last-90-days', name: 'Last 90 days' },
      { id: 'last-quarter', name: 'Last Quarter' },
      { id: 'year-to-date', name: 'Year To Date' },
      { id: 'start-to-date', name: 'Start To Date' }
    ]

    const todayStart: Dayjs = dayjs().utc().startOf('day')
    const todayEnd: Dayjs = dayjs().utc().endOf('day')
    let startDate: Dayjs, endDate: Dayjs

    const dateListWithRanges = dateList.map(selected => {
      switch (selected.id) {
        case 'last-week':
          startDate = dayjs().utc().startOf('week').subtract(1, 'week')
          endDate = dayjs().utc().endOf('week').subtract(1, 'week')
          break

        case 'last-30-days':
          startDate = todayStart.subtract(30, 'days')
          endDate = todayEnd
          break

        case 'last-90-days':
          startDate = todayStart.subtract(90, 'days')
          endDate = todayEnd
          break

        case 'last-quarter':
          startDate = dayjs().utc().startOf('quarter').subtract(1, 'quarter')
          endDate = dayjs().utc().endOf('quarter').subtract(1, 'quarter')
          break

        case 'year-to-date':
          startDate = dayjs().utc().startOf('year')
          endDate = todayEnd
          break

        case 'start-to-date':
          endDate = todayEnd
          break

        default:
          break
      }
      return { ...selected, startDate, endDate } as ReportDateRange
    })
    setDateOptions(dateListWithRanges)
  }

  return (
    <>
      <FilterContainer data-testid="filter-container">
        <DropDownFilter data-testid="location-filter">
          <ComboBox
            listItems={businessUnits as unknown as ListOption[]}
            listValue={selectedBU}
            setListValue={setSelectedBU}
            disabled={false}
            valueProperty="id"
            displayTemplate="{name}, {location}"
          />
        </DropDownFilter>
        <DropDownFilter data-testid="team-filter">
          <DropDown
            listItems={teamOptions as unknown as ListOption[]}
            listValue={selectedTeam}
            setListValue={setSelectedTeam}
            valueProperty="id"
            displayProperty="name"
          />
        </DropDownFilter>
        <DropDownFilter data-testid="date-range-filter">
          <DropDown
            listItems={dateOptions as unknown as ListOption[]}
            listValue={selectedDate}
            setListValue={setSelectedDate}
            valueProperty="id"
            displayProperty="name"
          />
        </DropDownFilter>
      </FilterContainer>
    </>
  )
}

export default ReportingFilter
